import {
	REQUEST,
	SUCCESS,
	SET_ENTITLEMENTS,
	FAILED,
	LOGGED_OUT,
	AVATAR_UPDATED,
	CLEAR_AVATAR,
	LOGO_UPDATED,
	CLEAR_ERRORS,
	REGISTER_SUCCESS,
	GOOGLE_AUTHENTICATED,
	GOOGLE_AUTH_FAILED,
	MAIL_RETRIEVED,
	GOOGLE_LOGOUT,
	CHANGE_DESK_IMAGE,
	UPDATE_ACHIEVEMENTS,
	SWITCH_COMPANY_REQUEST,
	SWITCH_COMPANY_SUCCESSFUL,
	SWITCH_COMPANY_FAILED,
	HOLD_REGISTER_STATE_VALUES,
	CLEAR_REGISTER_STATE_VALUES,
	CLEAR_MAIL_AUTH,
	GOOGLE_SIGNIN,
	AWAITINGMFA,
	UPDATE_USER,
	UPDATE_GOOGLE_ACCOUNT,
	GOOGLE_CALENDARS_ADDED,
	ADD_CALENDAR_TOKEN,
	SET_LAST_VALID_USER_SESSION,
	RESET_APP,
	REMOVE_INTEGRATION,
	HANDLE_FAVOURITE,
	REFRESH,
	ADD_INTEGRATION,
	SET_TUTORIAL_COMPLETE,
	SET_TUTORIAL_VERSION_COMPLETE,
	SYNC_USER_TUTORIALS,
	ADD_LOCAL_CALENDARS,
	ADD_LIKE_TO_POST,
	ADD_COMMENT_TO_POST,
	DELETE_COMMENT_FROM_POST,
	DELETE_LIKE_FROM_POST,
	UPDATE_GOOGLE_MAIL_TOKEN,
	REMOVE_MAIL_SCOPE,
	REFRESH_GOOGLE_TOKEN,
} from '../actions/types';
import { clearPersistState, updateObject } from './utils/globalStateFunctions';
import { FLUSH, PURGE } from 'redux-persist';
import { Platform } from 'react-native';
import { abortAllRequests } from '@utils/abortController';
import userData from '@navigation/screens/MidLevel/UsersScreen/Components/userData';
import { computeSessionExpireAt } from '@utils/sessionConstants';
import { getRememberMePreference } from '@utils/persistentSessionStorage';

const INITIAL_STATE = {
	loading: false,
	message: {},
	isAuthenticated: false,
	success: null,
	lastValidUserSession: null,
	data: {
		userData: {
			authentications: {} as { [key: string]: any },
			calendar: {},
			favourites: {
				projects: [],
				data: [],
				news: [],
			},
			posts: { liked_posts: [] },
		},
	},
};

const authReducer = (state = INITIAL_STATE, action) => {
	switch (action.type) {
		case SET_ENTITLEMENTS:
			return updateObject(state, {
				entitlements: action.payload,
			});
		case REMOVE_INTEGRATION: {
			const integration: string = action.payload;

			// Remove the specified key from the authentications object
			const { [integration]: _, ...rest } =
				state.data.userData.authentications;

			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						authentications: {
							...rest, // Use the updated authentications object without the removed key
						},
					},
				},
			});
		}
		case ADD_INTEGRATION:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						authentications: {
							...state.data.userData.authentications,
							[action.payload.authType]: action.payload.data,
						},
					},
				},
			});
		case CLEAR_MAIL_AUTH:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						authentications: {
							...state.data.userData.authentications,
							mailAuthenticated: false,
							mail: {},
						},
					},
				},
			});
		case REMOVE_MAIL_SCOPE:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						authentications: {
							...state.data.userData.authentications,
							mail: {
								...state.data.userData.authentications.mail,
								mailAuthenticated: false,
							},
							[action.payload.authType]: {
								[action.payload.authType.scopes]: [
									...state.data.userData.authentications[
										action.payload.authType.scopes
									].filter(
										(scope) => scope !== action.payload
									),
								],
							},
						},
					},
				},
			});
		case UPDATE_USER:
			return updateObject(state, {
				...state,
				data: {
					userData: {
						...state.data.userData,
						...action.payload,
						// Deep merge authentications if provided
						...(action.payload.authentications && {
							authentications: {
								...state.data.userData.authentications,
								...action.payload.authentications,
							},
						}),
						// Deep merge context if provided (for onboarding data)
						...(action.payload.context && {
							context: {
								...state.data.userData.context,
								...action.payload.context,
							},
						}),
						// Deep merge profile_information (avoid wiping name/avatar on partial patches)
						...(action.payload.profile_information && {
							profile_information: {
								...state.data.userData.profile_information,
								...action.payload.profile_information,
								...(action.payload.profile_information.context && {
									context: {
										...(state.data.userData.profile_information?.context || {}),
										...action.payload.profile_information.context,
									},
								}),
							},
						}),
					},
				},
			});
		case UPDATE_GOOGLE_MAIL_TOKEN: {
			const googleAuths = state.data.userData.authentications.google;
			let updatedGoogleAuths;
			
			if (Array.isArray(googleAuths)) {
				// If it's an array, update the specific account or the first one if payload doesn't specify
				let updatedFallback = false;
				updatedGoogleAuths = googleAuths.map(auth => {
					if (action.payload.email && auth.email === action.payload.email) {
						return { ...auth, accessToken: action.payload.newToken };
					} else if (!action.payload.email && auth.refreshToken && !updatedFallback) {
						// Fallback if no email provided, update the first one with a refresh token
						updatedFallback = true;
						return { ...auth, accessToken: action.payload.newToken };
					}
					return auth;
				});
			} else {
				// Legacy object support
				updatedGoogleAuths = {
					...googleAuths,
					accessToken: action.payload.newToken || action.payload,
				};
			}

			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						authentications: {
							...state.data.userData.authentications,
							google: updatedGoogleAuths,
						},
					},
				},
			});
		}
		case LOGGED_OUT:
			return updateObject(state, INITIAL_STATE);
		case HOLD_REGISTER_STATE_VALUES:
			const heldValues = action.payload.values;
			return updateObject(state, {
				heldValues: heldValues,
			});
		case CLEAR_REGISTER_STATE_VALUES:
			return updateObject(state, {
				heldValues: {},
			});
		case ADD_LIKE_TO_POST:
			// Clone the liked_posts array and push the new payload into it
			const newLikedPosts = [
				...state.data.userData.posts.liked_posts,
				action.payload,
			];

			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						posts: {
							...state.data.userData.posts,
							liked_posts: newLikedPosts,
						},
					},
				},
			});

		case DELETE_LIKE_FROM_POST:
			const updatedLikedPosts =
				state.data.userData.posts.liked_posts.filter(
					(post) => post !== action.payload
				);

			const likedPosts =
				updatedLikedPosts.length > 0 ? updatedLikedPosts : [];

			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						posts: {
							...state.data.userData.posts,
							liked_posts: likedPosts,
						},
					},
				},
			});
		case HANDLE_FAVOURITE:
			const { id, type, body } = action.payload;
			console.log('🎯 HANDLE_FAVOURITE:', id, type, body);

			// Determine the item to search for, using body if it exists, otherwise id
			const item = body ? body : id;

			// Get current favourites array for the type (or empty array if doesn't exist)
			const currentFavourites =
				state.data.userData.favourites?.[type] || [];

			// Check if the item already exists in the favourites array
			const existingIndex = currentFavourites.indexOf(item);

			// Create new favourites array (immutable update)
			let newFavourites;
			if (existingIndex !== -1) {
				// Item exists, remove it from the array
				newFavourites = [
					...currentFavourites.slice(0, existingIndex),
					...currentFavourites.slice(existingIndex + 1),
				];
				console.log('❌ REMOVED FAVOURITE:', item, 'from', type);
			} else {
				// Item does not exist, add it to the array
				newFavourites = [...currentFavourites, item];
				console.log('✅ ADDED FAVOURITE:', item, 'to', type);
			}

			// Return completely new state object (no mutations)
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						favourites: {
							...state.data.userData.favourites,
							[type]: newFavourites,
						},
					},
				},
			});
		case SET_TUTORIAL_COMPLETE:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						tutorials: {
							...state.data.userData.tutorials,
							firstTutorial: true,
							tutorialModal: true, // Also set tutorialModal when firstTutorial is set
						},
					},
				},
			});
		case SET_TUTORIAL_VERSION_COMPLETE:
			const { tutorialId, versionCode } = action.payload;
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						tutorials: {
							...state.data.userData.tutorials,
							completed_tutorials: {
								...(state.data.userData.tutorials?.completed_tutorials || {}),
								[tutorialId]: versionCode,
							},
							// Also update legacy flags for compatibility
							...(tutorialId === 'appTour' || tutorialId === 'firstTutorial' ? { firstTutorial: true } : {}),
							...(tutorialId === 'tutorialModal' ? { tutorialModal: true } : {}),
							// Update requiredTutorials flag if it exists in state
							requiredTutorials: {
								...(state.data.userData.tutorials?.requiredTutorials || {}),
								[tutorialId]: false,
							}
						},
					},
				},
			});
		case SYNC_USER_TUTORIALS: {
			if (!state.data?.userData || !action.payload) return state;
			const currentTutorials = state.data.userData.tutorials || {};
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						tutorials: {
							...currentTutorials,
							...action.payload,
							completed_tutorials: {
								...(currentTutorials.completed_tutorials || {}),
								...(action.payload.completed_tutorials || {}),
							},
						},
					},
				},
			});
		}
		case RESET_APP:
			return updateObject(state, INITIAL_STATE);
		case SET_LAST_VALID_USER_SESSION: {
			const session = action.payload || null;
			const hasExpiry = Boolean(session?.expireAt);
			const isExpired =
				hasExpiry && new Date(session.expireAt) <= new Date();
			return updateObject(state, {
				lastValidUserSession: isExpired ? null : session,
			});
		}
		case UPDATE_ACHIEVEMENTS:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						achievements: {
							...state.data.userData.achievements,
							achievementPoints: action.payload,
						},
					},
				},
			});
		case REFRESH: {
			const incomingProfile = action.payload?.profile_information;
			const profile_information = incomingProfile
				? {
						...(state.data.userData.profile_information || {}),
						...incomingProfile,
						...(incomingProfile.context
							? {
									context: {
										...(state.data.userData.profile_information?.context || {}),
										...incomingProfile.context,
									},
							  }
							: {}),
				  }
				: state.data.userData.profile_information;
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						achievements: {
							...state.data.userData.achievements,
							achievementPoints: action.payload.achievements,
						},
						favourites: {
							...state.data.userData.favourites,
							favourites: action.payload.favourites,
						},
						profile_information,
						billing_info: action.payload.billing_info,
						posts: {
							...state.data.userData.posts,
							posts: action.payload.posts,
						},
						...(action.payload.mutedConversationIds !== undefined && {
							mutedConversationIds: action.payload.mutedConversationIds,
						}),
					},
				},
			});
		}
		case REFRESH_GOOGLE_TOKEN:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						authentications: {
							...state.data.userData.authentications,
							google: {
								...state.data.userData.authentications.google,
								accessToken: action.payload,
							},
						},
					},
				},
			});
		case UPDATE_GOOGLE_ACCOUNT: {
			const rawScopes = action.payload?.scopes;
			const gmailMailGranted =
				Array.isArray(rawScopes) &&
				rawScopes.some((s: unknown) => String(s).includes('gmail.modify'));
			const gmailMailGrantedStr =
				typeof rawScopes === 'string' && rawScopes.includes('gmail.modify');
			const hasGmailMailScope = Boolean(gmailMailGranted || gmailMailGrantedStr);
			
			let googleAuths = state.data.userData.authentications.google;
			let updatedGoogleAuths;

			if (Array.isArray(googleAuths)) {
				const existingIndex = googleAuths.findIndex(auth => auth.email === action.payload.email || auth.googleId === action.payload.googleId);
				if (existingIndex >= 0) {
					updatedGoogleAuths = [...googleAuths];
					const existing = updatedGoogleAuths[existingIndex];
					const existingScopes = Array.isArray(existing.scopes) ? existing.scopes : (typeof existing.scopes === 'string' ? existing.scopes.split(' ') : []);
					const newScopes = Array.isArray(action.payload.scopes) ? action.payload.scopes : (typeof action.payload.scopes === 'string' ? action.payload.scopes.split(' ') : []);
					const mergedScopes = Array.from(new Set([...existingScopes, ...newScopes]));
					updatedGoogleAuths[existingIndex] = {
						...existing,
						...action.payload,
						scopes: mergedScopes,
					};
				} else {
					updatedGoogleAuths = [...googleAuths, action.payload];
				}
			} else if (googleAuths) {
				// Legacy object support
				if (googleAuths.email === action.payload.email || googleAuths.googleId === action.payload.googleId) {
					const legacyScopes = Array.isArray(googleAuths.scopes) ? googleAuths.scopes : (typeof googleAuths.scopes === 'string' ? googleAuths.scopes.split(' ') : []);
					const newPayloadScopes = Array.isArray(action.payload.scopes) ? action.payload.scopes : (typeof action.payload.scopes === 'string' ? action.payload.scopes.split(' ') : []);
					const mergedScopes = Array.from(new Set([...legacyScopes, ...newPayloadScopes]));
					updatedGoogleAuths = [{ ...googleAuths, ...action.payload, scopes: mergedScopes }];
				} else {
					updatedGoogleAuths = [googleAuths, action.payload];
				}
			} else {
				updatedGoogleAuths = [action.payload];
			}

			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						authentications: {
							...state.data.userData.authentications,
							mail: {
								...state.data.userData.authentications.mail,
								mailAuthenticated: hasGmailMailScope || state.data.userData.authentications.mail?.mailAuthenticated,
								mailType: hasGmailMailScope
									? 'google'
									: state.data.userData.authentications.mail
											?.mailType || '',
							},
							google: updatedGoogleAuths,
						},
					},
				},
			});
		}
		// if (Platform.OS === 'web') {
		// 	window.location.reload();
		// } else {
		// 	return;
		// }
		case ADD_CALENDAR_TOKEN:
			return updateObject(state, {
				google: {
					...state.google,
					gCal: action.payload,
				},
			});
		case SWITCH_COMPANY_REQUEST:
			//Cancel all active network requests
			return updateObject(state, {
				loading: true,
			});
		case SWITCH_COMPANY_SUCCESSFUL:
			// Handle different switch types: user, company, or personal
			const switchType = action.payload?.switchType;
			if (switchType === 'user') {
				// User switch - keep authenticated user id; targetUserId scopes read/write context
				const originalUserId =
					action.payload.originalUserId || state.data?.userData?.id;
				return updateObject(state, {
					loading: false,
					data: {
						...state.data,
						companyData: null,
						userData: {
							...state.data?.userData,
							id: originalUserId,
							originalUserId,
							targetUserId: action.payload.targetUserId,
							targetUser: action.payload.targetUser,
						},
					},
				});
			} else if (switchType === 'personal') {
				// Personal account switch - clear companyData and targetUser info
				const updatedUserData = state.data?.userData
					? { ...state.data.userData }
					: null;
				if (updatedUserData) {
					// Explicitly remove targetUserId and targetUser properties
					delete updatedUserData.targetUserId;
					delete updatedUserData.targetUser;
				}
				return updateObject(state, {
					loading: false,
					data: {
						...state.data,
						companyData: null,
						userData: updatedUserData || state.data?.userData,
					},
				});
			} else {
				// Company switch - update companyData
				return updateObject(state, {
					loading: false,
					data: {
						...state.data,
						companyData: action.payload.companyData,
					},
				});
			}
		case SWITCH_COMPANY_FAILED:
			return updateObject(state, {
				loading: false,
			});
		case REQUEST:
			return updateObject(state, {
				loading: true,
			});
		case MAIL_RETRIEVED:
			return updateObject(state, {
				mailRetrieved: Date.now(),
			});
		case GOOGLE_AUTH_FAILED:
			return updateObject(state, {
				google: {
					...state.google,
					googleAuthenticated: false,
					googleCredentials: null,
				},
			});
		case GOOGLE_LOGOUT:
			return updateObject(state, {
				google: {
					...state.google,
					googleAuthenticated: false,
					googleCredentials: null,
					gMail: false,
					gCal: false,
					gDrive: false,
				},
				mailRetrieved: null,
			});
		case SUCCESS:
			const successRememberMe =
				action.data?.rememberMe ?? getRememberMePreference();
			const successSession = action.data
				? {
						...action.data,
						rememberMe: successRememberMe,
						expireAt:
							action.data.expireAt ||
							computeSessionExpireAt(successRememberMe),
				  }
				: null;
			return updateObject(state, {
				...state,
				message: {},
				data: action.data,
				lastValidUserSession: successSession,
				loading: false,
				isAuthenticated: true,
			});
		case AWAITINGMFA:
			return updateObject(state, {
				message: {},
				data: action.data,
				loading: false,
				isAuthenticated: false,
			});
		case CLEAR_AVATAR:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						avatar: {
							...state.data.userData.avatar,
							img: undefined,
						},
					},
				},
			});
		case AVATAR_UPDATED:
			updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						avatar: {
							...state.data.userData.avatar,
							img: '',
						},
					},
				},
			});
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						avatar: {
							...state.data.userData.avatar,
							img: action.data,
						},
					},
				},
			});
		case LOGO_UPDATED:
			return updateObject(state, {
				data: {
					...state.data,
					companyData: {
						...state.data.companyData,
						companyLogo: {
							...state.data.companyData.companyLogo,
							img: action.data,
						},
					},
				},
			});
		case CHANGE_DESK_IMAGE:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						dashboardImage: {
							...state.data.userData.dashboardImage,
							img: action.data,
						},
					},
				},
			});
		case GOOGLE_AUTHENTICATED:
			return updateObject(state, {
				google: {
					...state.google,
					googleAuthenticated: true,
					googleCredentials: action.payload,
				},
			});
		case REGISTER_SUCCESS:
			return updateObject(state, {
				message: action.payload.message,
			});
		case FAILED:
			return updateObject(state, {
				error: action.payload.message,
			});
		case CLEAR_ERRORS:
			clearPersistState();
			return updateObject(state, {
				error: {},
			});
		default:
			return state;
	}
};

export default authReducer;
